<script>
import { mapGetters } from 'vuex'
import LocationResource from '@/resources/location'
import CalendarResource from '@/resources/calendar'
import { RacePresetResource, PartyResource, PartyMemberResource, RaceEventResource } from '@/resources/launcher'
import PartyDetail from '../components/PartyDetail.vue'
import EventForm from '../components/EventForm.vue'

export default {
    components: {
        PartyDetail,
        EventForm
    },
    computed: {
        ...mapGetters(['userResource']),
        resources() {
            return {}
        },
        calendarsIndexed() {
            const mapped = {}

            for (let key in this.calendars) {
                mapped[this.calendars[key].id] = this.calendars[key]
            }

            return mapped
        },
        calendarIdsValid() {
            if (!this.calendars.length) {
                return []
            }

            let calendarsInParties = []
            let validIds = []
            
            for (let party of this.parties) {
                calendarsInParties = calendarsInParties.concat(party.attributes.members.map((member) => member.calendar))
            }

            for (let calendar of this.calendars) {
                if (calendar.attributes.event && !calendarsInParties.includes(calendar.id)) {
                    validIds.push(calendar.id)
                }
            }
            
            return validIds
        },
        calendarIdsSelected() {
            return this.selectedCalendars.map((calendar) => calendar.id)
        },
        selectedCalendarsHost() {
            return this.selectedCalendars[0]
        },
        selectedCalendarsOthers() {
            let calendars = []

            for (let i in this.selectedCalendars) {
                if (i == 0) {
                    continue
                }

                calendars.push(this.selectedCalendars[i])
            }

            return calendars
        },
    },
    watch: {
        eventEditSelectedTrack() {
            this.eventEdit.track = this.eventEditSelectedTrack?.id || null
        },
        zoomLevel() {
            localStorage.setItem('adminRaceControlZoom', this.zoomLevel)
        },
    },
    data() {
        return {
            loading: true,
            calendars: [],
            locations: [],
            presets: [],
            parties: [],
            partyCreating: false,
            selectedLocation: null,
            selectedCalendars: [],
            selectedPartyToAdd: null,
            selectedEventToEdit: null,
            cells: [],
            createPartyName: '',
            zoomLevel: parseFloat(localStorage.getItem('adminRaceControlZoom')) || 1,
            intervalIDParty: null
        }
    },
    methods: {
        async load() {
            await this.loadGlobals()
            await this.loadFiltered()
            this.intervalIDParty = setInterval(() => this.fetchParties(), 1e4)

            const socketPromises = this.calendars.map((calendar) => calendar.socketConnect())

            await Promise.all(socketPromises)

            for (let calendar of this.calendars) {
                calendar.emitter.on('message', (event) => this.onCalendarMessage(calendar, event))
                // Ask each connected launcher to report its current status immediately,
                // rather than waiting on it to push one on its own -- otherwise this screen
                // shows stale/unknown running state until something unrelated happens to
                // change on the launcher side.
                calendar.socketSendStatusRequest()
            }

            this.loading = false
        },
        async loadGlobals() {
            return await Promise.all([
                this.fetchLocations(),
                this.fetchPresets()
            ])
        },
        async loadFiltered() {
            return await Promise.all([
                this.fetchCalendars(),
                this.fetchParties(),
            ])
        },
        async fetchLocations() {
            this.locations = await LocationResource.all()

            if (this.$route.query.location) {
                this.selectedLocation = this.locations.find((location) => location.id == this.$route.query.location)
            } else {
                this.selectedLocation = this.locations.find((location) => location.id == this.userResource.attributes.location)
            }
        },
        async fetchPresets() {
            this.presets = await RacePresetResource.all()
            return this.presets
        },
        async fetchParties() {
            this.parties = await PartyResource.filterAll({ location: this.selectedLocation.id, ordering: '' }, { resolveRelated: true })
            return this.parties
        },
        async fetchCalendars() {
            this.calendars = await CalendarResource.filterAll({ location: this.selectedLocation.id })
            return this.calendars
        },
        selectCalendar(calendarResource) {
            if (this.calendarIdsSelected.includes(calendarResource.id)) {
                const calendarIndex = this.selectedCalendars.findIndex((calendar) => calendar.id == calendarResource.id)
                this.selectedCalendars.splice(calendarIndex, 1)
            } else {
                this.selectedCalendars.push(calendarResource)
            }
        },
        onPartyDelete(partyResource) {
            const index = this.parties.findIndex((party) => party.id == partyResource.id)
            this.parties.splice(index, 1)
        },
        onCreateParty() {
            this.createPartyName = `${String(this.selectedCalendarsHost)}'s Party`
            this.$refs.createPartyModal.show()
        },
        onAddToParty() {
            this.selectedPartyToAdd = this.parties[0]
            this.$refs.addToPartyModal.show()
        },
        onCalendarAddToParty(calendarResource) {
            this.selectedCalendars = [calendarResource]
            this.onAddToParty()
        },
        onCalendarMessage(calendarResource, eventData) {
            if (eventData.type == 'signal' && eventData.value == 'SERVER_ERROR') {
                calendarResource.hasServerError = true
            } else if (eventData.type == 'status') {
                calendarResource.hasServerError = false
                calendarResource.isRunning = eventData.value.acsRunning
            } else if (eventData.type == 'signal' && eventData.value == 'SESSION_STARTED')  {
                calendarResource.isRunning = true
            } else if (eventData.type == 'signal' && eventData.value == 'SESSION_STOPPED')  {
                calendarResource.isRunning = false
            }
        },
        onBoot(calendarId) {
            window.location.reload()
        },
        async createParty() {
            // const otherMemberCalendarIds = this.selectedCalendarsOthers.map((calendar) => calendar.id)
            // const party = await PartyResource.host(this.selectedCalendarsHost.id, this.createPartyName, otherMemberCalendarIds)

            // if (party) {
            //     await this.reloadSelectedCalendars()
            //     await party.update()
            //     this.parties.push(party)
            //     this.selectedCalendars = []
            // }
            this.partyCreating = true
            const party = await PartyResource.host(this.selectedCalendarsHost.id, this.createPartyName)
            const otherMembersPromises = []

            setTimeout(async () => {
                for (let calendar of this.selectedCalendarsOthers) {
                    otherMembersPromises.push(party.addMember(calendar.id))
                }

                await Promise.all(otherMembersPromises)
                await this.reloadSelectedCalendars()
                this.selectedCalendars = []
                this.parties = []
                await this.loadFiltered()
                this.partyCreating = false
            }, 3000)
        },
        async addToParty() {
            const membershipPromises = []

            for (let calendar of this.selectedCalendars) {
                const membership = new PartyMemberResource({
                    party: this.selectedPartyToAdd.id,
                    calendar: calendar.id
                })

                membershipPromises.push(membership.save())
            }

            await Promise.all(membershipPromises).then(async () => {
                await this.reloadSelectedCalendars()
                window.location.reload()
            })
        },
        async editEventForCalendar(calendarResource) {
            this.selectedEventToEdit = await RaceEventResource.detail(calendarResource.attributes.event)
            this.selectedEventToEdit.socketWatchForChanges()
        },
        onEventEditModalHide() {
            if (this.selectedEventToEdit) {
                this.selectedEventToEdit.socketClose()
            }

            this.selectedEventToEdit = null
        },
        async startOnCalendar(calendarResource) {
            await calendarResource.socketConnect()
            calendarResource.socketSendSignal('SESSION_START')
        },
        async stopOnCalendar(calendarResource) {
            await calendarResource.socketConnect()
            calendarResource.socketSendSignal('SESSION_EXIT')
        },
        async reloadCalendar(calendarResource) {
            await calendarResource.socketConnect()
            calendarResource.socketSendSignal('RELOAD')
        },
        async reloadSelectedCalendars() {
            for (let calendar of this.selectedCalendars) {
                await calendar.socketConnect()
                calendar.socketSendSignal('RELOAD')
            }
        },
        async rebuildRaceData() {
            await RaceEventResource.rebuild()

            for (let calendar of this.calendars) {
                await calendar.socketConnect()
                calendar.socketSendSignal('RELOAD')
            }

            setTimeout(() => {
                window.location.reload()
            }, 3000)
        }
    },
    async mounted() {
        await this.load()
    },
    async beforeDestroy() {
        if (this.intervalIDParty) {
            clearInterval(this.intervalIDParty)
        }

        // Pairs with the calendar.socketConnect() calls in load() -- safe to always call
        // since the connection is pooled/refcounted per calendar id (see
        // resources/socketResource.js), so this only actually closes a socket once every
        // other component sharing that calendar id (e.g. an EventForm.vue for a party
        // containing this seat) has also released it.
        for (let calendar of this.calendars) {
            calendar.socketClose()
        }

        if (this.selectedEventToEdit) {
            this.selectedEventToEdit.socketClose()
        }
    }
}
</script>

<template>
    <b-container fluid>
        <b-row class="mt-3">
            <b-col>
                <b-row>
                    <b-col>
                        <b-dropdown split :text="(!selectedCalendars.length) ? 'Créer une fête' : `Créer une fête (${selectedCalendars.length})`" :disabled="calendarIdsSelected.length == 0 || partyCreating" @click="onCreateParty()">
                            <b-dropdown-header>Avec les simulateurs sélectionnés</b-dropdown-header>
                            <b-dropdown-item @click="onAddToParty()" :disabled="parties.length < 1">Ajouter à la fête</b-dropdown-item>
                        </b-dropdown>
                    </b-col>
                    <b-col md="4">
                        <b-dropdown class="float-right ml-2" variant="outline-primary" no-caret right>
                            <template slot="button-content">
                                <b-icon icon="gear" />
                            </template>
                            <b-dropdown-header>Actions de débogage</b-dropdown-header>
                            <b-dropdown-item @click="$refs.rebuildRaceData.show()">Reconstruire les données de l'événement de course</b-dropdown-item>
                        </b-dropdown>
                        <b-spinbutton v-model="zoomLevel" step="0.1" min="0.5" max="1" class="col-md-6 float-right" :formatter-fn="(value) => `Zoom : ${value}`" />
                    </b-col>
                </b-row>
            </b-col>
        </b-row>
        <hr />
        <b-row class="mt-3" v-if="!loading">
            <b-col md="2">
                <!-- <b-button v-for="calendar in calendars" :key="calendar.id" @click="selectCalendar(calendar)" :disabled="!calendarIdsValid.includes(calendar.id)" :variant="(calendarIdsSelected.includes(calendar.id)) ? 'secondary' : !calendarIdsValid.includes(calendar.id) ? 'light' : 'outline-primary'" size="sm" class="col-sm-12 mb-2">
                    {{ calendar }}
                </b-button> -->
                <b-dropdown 
                    v-for="calendar in calendars"
                    :key="calendar.id"
                    :disabled="!calendarIdsValid.includes(calendar.id) || partyCreating"
                    @click="selectCalendar(calendar)"
                    split
                    split-class="split-sim-name"
                    right
                    :variant="(calendarIdsSelected.includes(calendar.id)) ? 'secondary' : !calendarIdsValid.includes(calendar.id) ? 'light' : 'outline-primary'"
                    size="sm"
                    class="col-sm-12 mb-2">
                    <template slot="button-content">
                        {{ calendar }}
                        <b-icon v-if="calendar.hasServerError" icon="exclamation-diamond-fill" variant="danger" />
                        <b-icon v-else-if="calendar.isRunning" icon="hdd-network" class="float-right" />
                    </template>
                    <b-dropdown-item @click="selectCalendar(calendar)">Sélectionner</b-dropdown-item>
                    <b-dropdown-divider />
                    <b-dropdown-header>Contrôle</b-dropdown-header>
                    <b-dropdown-item variant="success" @click="startOnCalendar(calendar)">Démarrer la session</b-dropdown-item>
                    <b-dropdown-item variant="danger" @click="stopOnCalendar(calendar)">Arrêter la session</b-dropdown-item>
                    <b-dropdown-item @click="reloadCalendar(calendar)">Recharger</b-dropdown-item>
                    <b-dropdown-divider />
                    <b-dropdown-header>Événement</b-dropdown-header>
                    <b-dropdown-item @click="editEventForCalendar(calendar)">Modifier l'événement</b-dropdown-item>
                    <b-dropdown-divider />
                    <b-dropdown-header>Fête</b-dropdown-header>
                    <b-dropdown-item @click="onCalendarAddToParty(calendar)" :disabled="parties.length < 1">Ajouter à une fête</b-dropdown-item>
                </b-dropdown>
            </b-col>
            <b-col>
                <b-row v-if="parties.length > 0" class="mb-3">
                    <b-col v-for="party in parties" :key="party.id" xl="5" lg="6" sm="12">
                        <div :style="`zoom: ${zoomLevel}`">
                            <party-detail
                                ref="partyComponent"
                                :party="party"
                                @deleteParty="onPartyDelete(party)"
                                @boot="(calendarId) => onBoot(calendarId)"
                                class="mb-3">
                            </party-detail>
                        </div>
                    </b-col>
                </b-row>

                <b-row v-else>
                    <b-col xl="4" lg="6" sm="12">
                        <b-card class="text-center text-muted">
                            <div class="py-5">
                                <p>Aucune fête n'a encore été créée.</p>
                                <p><small>Sélectionnez des sièges à gauche pour commencer.</small></p>
                            </div>
                        </b-card>
                    </b-col>
                </b-row>
            </b-col>
        </b-row>

        <div v-else class="text-center text-muted">
            <b-spinner type="grow" label="Chargement..." class="mt-5 mb-5" />
        </div>

        <b-modal ref="createPartyModal" title="Créer une fête" @ok="createParty()" ok-title="Créer une fête" :ok-disabled="!createPartyName" no-stacking no-close-on-backdrop scrollable centered no-fade>
            <b-form-group label="Nom de la fête :">
                <b-form-input v-model="createPartyName" />
            </b-form-group>
            <hr />
            Sièges :
            <b-row>
                <b-col md="6">
                    <b-card body-class="py-2" class="mb-2">
                        <strong>{{ selectedCalendarsHost }} <b-icon icon="hdd" /></strong>
                    </b-card>
                </b-col>
                <b-col v-for="calendar in selectedCalendarsOthers" :key="calendar.id" md="6">
                    <b-card body-class="py-2" class="mb-2">
                        {{ calendar }}
                    </b-card>
                </b-col>
            </b-row>
        </b-modal>

        <b-modal ref="addToPartyModal" title="Ajouter à une fête" @ok="addToParty()" ok-title="Ajouter à une fête" :ok-disabled="!selectedPartyToAdd" no-stacking no-close-on-backdrop scrollable centered no-fade>
            <b-form-select v-model="selectedPartyToAdd">
                <b-form-select-option v-for="party in parties" :key="party.id" :value="party">{{ party }}</b-form-select-option>
            </b-form-select>
        </b-modal>

        <b-modal title="Modifier l'événement" v-if="selectedEventToEdit" visible @hide="onEventEditModalHide()" @ok="$refs.eventComponent.save()" no-stacking no-close-on-backdrop scrollable centered no-fade>
            <event-form ref="eventComponent" :event="selectedEventToEdit" no-save />
        </b-modal>

        <b-modal title="Reconstruire les données de l'événement de course ?" ref="rebuildRaceData" @ok="rebuildRaceData()" no-stacking no-close-on-backdrop scrollable centered no-fade>
            Êtes-vous sûr de vouloir reconstruire les données de l'événement de course ? Cela dissoudra toutes les fêtes actives. Vous devrez peut-être également recharger manuellement tous les lanceurs.
        </b-modal>
        
    </b-container>
</template>

<style lang="scss">
.in-party {
    font-weight: bold;
}

.split-sim-name {
    text-align: left !important;
    width: 80%;
}
</style>
